import type { CaseDetailView } from '@concord/domain/contracts';
import { Button, Dialog, Field, Notice } from '@concord/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useCaseAction } from './CaseControls';

export interface NavigatorOption {
  id: string;
  name: string;
  email: string;
  openCases: number;
}

export const navigatorLabel = (n: NavigatorOption) =>
  `${n.name} · ${n.openCases} open ${n.openCases === 1 ? 'case' : 'cases'}`;

/** Give the case to any active care navigator. The patient's contact card follows the owner. */
export function ReassignCaseDialog({ c, onDone }: { c: CaseDetailView; onDone: () => void }) {
  const act = useCaseAction(c);
  const navs = useQuery({
    queryKey: ['navigators', c.reference],
    queryFn: () => api<{ items: NavigatorOption[] }>(`/staff/cases/${c.reference}/navigators`),
  });
  const [to, setTo] = useState('');
  const choice = to || (navs.data?.items.find((n) => n.id !== c.owner?.id)?.id ?? '');
  const name = navs.data?.items.find((n) => n.id === choice)?.name;
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onDone()}
      title={`Reassign ${c.reference}`}
      description={`Now owned by ${c.owner?.name ?? 'no one'}. The new owner becomes the patient's care navigator on their home page and on physician invitations. The change is logged on the case.`}
      footer={
        <>
          <Button variant="sec" onClick={onDone}>
            Cancel
          </Button>
          <Button
            busy={act.isPending}
            disabled={!choice || choice === c.owner?.id}
            onClick={() =>
              act.mutate(
                { path: '/owner', body: { accountId: choice }, done: `Reassigned to ${name}.` },
                { onSuccess: onDone },
              )
            }
          >
            Reassign
          </Button>
        </>
      }
    >
      <Field label="New owner">
        {(id) => (
          <select id={id} className="select" value={choice} onChange={(e) => setTo(e.target.value)}>
            {navs.data?.items.map((n) => (
              <option key={n.id} value={n.id} disabled={n.id === c.owner?.id}>
                {navigatorLabel(n)}
                {n.id === c.owner?.id ? ' (current owner)' : ''}
              </option>
            ))}
          </select>
        )}
      </Field>
      {navs.isError && <Notice tone="bad">{errorMessage(navs.error)}</Notice>}
      {act.isError && <Notice tone="bad">{errorMessage(act.error)}</Notice>}
    </Dialog>
  );
}
